import React, { createContext, useContext, useEffect, useState } from 'react'
import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes'

// Custom Theme Context for additional functionality
interface ThemeContextType {
  isDarkMode: boolean
  theme: string | undefined
  toggleTheme: () => void
  setLightMode: () => void
  setDarkMode: () => void
  setSystemMode: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Custom ThemeProvider component
export function ThemeProvider({ children, ...props }: React.ComponentProps<typeof NextThemesProvider>) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      storageKey="evoltbay-theme"
      {...props}
    >
      <ThemeProviderInner>{children}</ThemeProviderInner>
    </NextThemesProvider>
  )
}

// Inner component that provides additional theme functionality
function ThemeProviderInner({ children }: { children: React.ReactNode }) {
  const { setTheme, resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  // Prevent hydration mismatch by only rendering after mount
  useEffect(() => {
    setMounted(true)
  }, [])

  const isDarkMode = resolvedTheme === 'dark'

  const toggleTheme = () => {
    setTheme(isDarkMode ? 'light' : 'dark')
  }

  const setLightMode = () => {
    setTheme('light')
  }

  const setDarkMode = () => {
    setTheme('dark')
  }

  const setSystemMode = () => {
    setTheme('system')
  }

  const contextValue: ThemeContextType = {
    isDarkMode,
    theme: resolvedTheme,
    toggleTheme,
    setLightMode,
    setDarkMode,
    setSystemMode,
  }

  // Prevent hydration mismatch by not rendering until mounted
  if (!mounted) {
    return <>{children}</>
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  )
}

// Custom hook to use theme context
export function useThemeContext() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider')
  }
  return context
}

// Re-export the useTheme hook from next-themes for convenience
export { useTheme }